import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/Layout/MainLayout';
import { Send, Bot, User, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useIsMobile } from '../hooks/use-mobile';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/lib/apiError';
import { ChatbotConsentBanner } from '@/components/ChatbotConsentBanner';
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const Chatbot: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [hasConsent, setHasConsent] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: t('chatbot.greeting'),
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useIsMobile();

  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!hasConsent) {
      toast({
        title: t('chatbot.consentRequired'),
        description: t('chatbot.consentRequiredDesc'),
        variant: "destructive"
      });
      return;
    }

    if (inputMessage.trim() && !isLoading) {
      const userMessage: Message = {
        id: Date.now().toString(),
        content: inputMessage,
        sender: 'user',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');
      setIsLoading(true);

      try {
        console.log('Sending message to chatbot:', inputMessage);

        // Build full conversation history (exclude initial bot greeting)
        const conversationHistory = messages
          .filter(m => m.id !== '1') // Exclude initial bot greeting
          .map(m => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.content
          }));

        const { data, error } = await supabase.functions.invoke('chatbot', {
          body: {
            message: inputMessage,
            conversationHistory // Pass context to the chatbot
          }
        });

        if (error) {
          console.error('Chatbot error:', error);
          throw error;
        }

        // 200-with-error body: function returned OK status but signalled a failure.
        if (data?.error || data?.success === false) {
          console.error('Chatbot returned an error body:', data);
          throw new Error(getErrorMessage(data, 'Failed to get response.'));
        }

        console.log('Chatbot response:', data);
        
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: data.reply || t('chatbot.defaultReply'),
          sender: 'bot',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botResponse]);
      } catch (error) {
        console.error('Error calling chatbot:', error);
        toast({
          title: t('chatbot.errorTitle'),
          description: t('chatbot.errorDesc'),
          variant: "destructive"
        });

        // Fallback message
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: t('chatbot.connectionTrouble'),
          sender: 'bot',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botResponse]);
      }
      
      setIsLoading(false);
    }
  };

  return (
    <MainLayout title={t('chatbot.title')}>
      <div className="flex flex-col h-[calc(100vh-8rem)]">
        {/* Consent Banner */}
        {!hasConsent && (
          <div className="flex-shrink-0 p-4 border-b border-border">
            <ChatbotConsentBanner onConsentGranted={() => setHasConsent(true)} />
          </div>
        )}
        
        {/* Messages - Scrollable area */}
        <div className={`flex-1 overflow-y-auto ${isMobile ? 'p-4' : 'p-6'} space-y-4`}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-start ${isMobile ? 'space-x-3' : 'space-x-4'} ${
                message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              <div className={`flex-shrink-0 ${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full flex items-center justify-center ${
                message.sender === 'user' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {message.sender === 'user' ? <User size={isMobile ? 16 : 20} /> : <Bot size={isMobile ? 16 : 20} />}
              </div>
              <div className={`${isMobile ? 'max-w-[85%]' : 'max-w-2xl'} px-4 py-3 rounded-lg break-words ${
                message.sender === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-foreground'
              }`}>
                <div className={`${isMobile ? 'text-sm' : 'text-base'} leading-relaxed prose prose-sm dark:prose-invert max-w-none`}>
                  <ReactMarkdown
                    components={{
                      p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2 space-y-1" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2 space-y-1" {...props} />,
                      li: ({node, ...props}) => <li className="ml-2" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-semibold" {...props} />,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className={`flex items-start ${isMobile ? 'space-x-3' : 'space-x-4'}`}>
              <div className={`flex-shrink-0 ${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full flex items-center justify-center bg-muted text-muted-foreground`}>
                <Bot size={isMobile ? 16 : 20} />
              </div>
              <div className={`${isMobile ? 'max-w-xs' : 'max-w-sm'} px-4 py-3 rounded-lg bg-secondary text-foreground`}>
                <div className="flex items-center space-x-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <p className={`${isMobile ? 'text-sm' : 'text-base'}`}>{t('chatbot.typing')}</p>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input - Fixed at bottom */}
        <div className={`flex-shrink-0 ${isMobile ? 'p-4' : 'p-6'} border-t border-border bg-background`}>
          <div className={`flex ${isMobile ? 'space-x-2' : 'space-x-4'}`}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder={t('chatbot.askPlaceholder')}
              className={`flex-1 ${isMobile ? 'px-4 py-2 text-sm' : 'px-6 py-3 text-base'} border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-background text-foreground`}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              className={`${isMobile ? 'px-4 py-2' : 'px-6 py-3'} bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[44px]`}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={isMobile ? 16 : 20} />}
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Chatbot;